import "./MyFirstRealVibe.scss";

import { VibeMessage } from "../components";
import PostWrapper from "../PostWrapper";
import { PostCategory } from "../postCategories";

function VibeTuneTooScreen() {
  return (
    <PostWrapper
      category={PostCategory.VibeCoding}
      subTitle="Making things more data-driven and DRYer"
      title="Vibe Tune Too"
    >
      <h3>So we're sticking with "Vibe Tune", eh?</h3>

      <p>
        Yes! It's as good a name as any! Now, let's talk about vibe tuning...
      </p>

      <p>
        The minute I realized the first refactor was going well (while still in
        the middle of it), I couldn't wait to start this one: the recipes
        refactor! This was bothering me every time I copy-pasted an existing
        recipe to then just change the content to the new recipe (and hope I
        didn't miss anything). I wanted something better.
      </p>

      <p>
        I knew (from experience, I suppose 😅) that the best thing to do here
        was to load in recipe data and use the existing core components to
        render it... but I also knew (from budget sheets and unwanted
        complexity) that I didn't want an actual backend and/or database to
        store and manage the data.
      </p>

      <h3>So, what did you do?</h3>

      <p>
        Great question! I decided to use JSON! I already vibe coded a script to
        add new recipes via JSON input, so I figured we should just move all
        recipes to be JSON in the first place and stop the horrible copy-paste
        workflow.
      </p>

      <p>
        A nice bonus here is that ChatGPT (just the web version) is quite good
        at generating recipe JSON from a link I provide, so when I do find a
        recipe I want to add, it's a very quick process end-to-end.
      </p>

      <p>
        Knowing what I wanted to do, I started the refactor with a concrete
        request for a "plan", and then tweaked the updates to match my end goal.
      </p>

      <p>This (still) feels like a good pattern.</p>

      <p>
        As long as we know what tech debt we have, or become aware of ways that
        we could improve our codebase through time and iteration, then we can do
        larger refactors with confidence and ease. (And it's that ease part that
        rocks 😎)
      </p>

      <h3>Everything written below was generated by OpenAI Codex</h3>

      <p>
        This thread started as a feasibility question and turned into a broader
        tuning session: recipes moved from repeated TSX components into JSON,
        the add-recipe workflow became smaller, and a couple of real-world
        follow-up bugs tightened the data validation and styling.
      </p>

      <div className="vibe-thread" aria-label="Conversation highlights">
        <VibeMessage from="Ryan">
          Determine the feasibility and usefulness of extracting all existing
          recipes to JSON files, and using those files to populate all the
          recipe content (i.e. moving away from a component per recipe and
          instead using a reusable component that get's populated via a JSON
          file for the recipe)
        </VibeMessage>
        <VibeMessage from="Codex">
          I inspected the recipe structure, route wiring, add-recipe script, and
          existing page content. The recipes were already mostly plain
          structured data, so JSON-driven rendering looked feasible and useful.
        </VibeMessage>

        <VibeMessage from="Codex">
          The plan was to keep every public food URL stable, add a reusable
          recipe renderer, move recipe content into one JSON file per recipe,
          derive the recipe registry from that data, update the add script, and
          cover the new data shape with tests.
        </VibeMessage>

        <VibeMessage from="Codex">
          Once Ryan asked me to implement the plan, I converted the existing
          recipe components into JSON data, added a reusable `RecipeScreen`,
          rewired `recipes.ts` to generate screens from data, removed the old
          per-recipe TSX files, updated the add script, and verified tests plus
          the production build.
        </VibeMessage>

        <VibeMessage from="Ryan">
          with this refactor, do we still need the script to add a recipe, or
          would adding a JSON recipe to the new data folder just work?
        </VibeMessage>
        <VibeMessage from="Codex">
          Adding only a JSON file did not work yet because `recipes.ts` still
          needed an import and `recipeData` entry. The script was still useful
          for those registry edits.
        </VibeMessage>

        <VibeMessage from="Ryan">
          should the script at least be using a path that IS already in the data
          directory (i.e. the script should assume the format is good and just
          update the non-JSON parts with the latest JSON file it was given)?
        </VibeMessage>
        <VibeMessage from="Codex">
          I changed the script so the JSON file in `src/screens/food/data` is
          the source of truth. It now validates the file location and minimal
          shape, then only updates the non-JSON registry pieces.
        </VibeMessage>

        <VibeMessage from="Ryan">
          The script updated `src/screens/food/recipes.ts`, but the new recipe
          (in this case it's from
          `src/screens/food/data/cola-braised-pork-butt.json`) did not show up
          on the food home page
        </VibeMessage>
        <VibeMessage from="Codex">
          I traced the new recipe through the registry and found that it was
          present, but its title matched an existing recipe. I made the new
          title distinct and added duplicate title/path checks in tests and the
          script.
        </VibeMessage>

        <VibeMessage from="Ryan">
          sometimes the links to the recipe source can cause horizontal
          scrolling, update those to allow character breaks so that we prevent
          that issue from happening
        </VibeMessage>
        <VibeMessage from="Codex">
          I scoped the recipe source link styling and added wrapping rules so
          long source URLs can break within the recipe page instead of forcing
          horizontal scrolling.
        </VibeMessage>
      </div>

      <p>
        The through line was making the content model match the actual workflow.
        Recipes were already data in practice; moving them to JSON made that
        explicit, then the follow-up prompts tuned the supporting script and UI
        details around that new source of truth.
      </p>

      <p className="vibe-handshake" aria-label="relieved">
        😌
      </p>
    </PostWrapper>
  );
}

export default VibeTuneTooScreen;
